import { analyzePricing, buildContext, computeRecipeCost, unitsSoldFor } from './costing';
import { revenueOf } from './finance';
import { DEFAULT_LOCATION_ID, type AdminTenantRow, type DailyRevenue, type ExpenseEntry, type Ingredient, type MenuItem, type OnboardingGoal, type Recipe, type TenantSettings } from './types';

/*
 * Activacion en dos hitos (Pol, 5 oct 2026; documento «Activación en Alexia:
 * dos hitos»).
 *
 * HITO 1 · ACTIVADO. Es el unico requisito para contar a alguien como
 * activado: ve la cuenta de resultados de un periodo real de su negocio con
 * datos que ha cargado el. En el periodo que esta mirando hay:
 *   1. al menos 7 dias seguidos con ventas reales (TPV, importacion o a mano),
 *   2. gastos (facturas) de ese mismo periodo que suman al menos el 30 % de
 *      esas ventas,
 *   3. y lo esta mirando el (Resultados o Analisis), no en una sesion de
 *      onboarding con Alex.
 * Las recetas NO forman parte de la activacion.
 *
 * HITO 2 · COSTEO ACTIVO. Se mide aparte y no es requisito: un local tiene el
 * costeo activo con al menos 5 platos que se venden con coste completo
 * (todos sus ingredientes con precio actualizado en los ultimos 60 dias). Es
 * un estado, no un hito que se gana una vez: si los precios envejecen, se
 * pierde. En unos meses se compara quien se queda; si los que no costean se
 * van, el costeo pasa a ser requisito.
 */

/** Provisional: se ajusta con los datos reales de Salomon y Leonardo. */
export const ACTIVATION_EXPENSE_RATIO = 0.3;
export const ACTIVATION_SALES_DAYS = 7;
export const ACTIVE_COSTING_DISHES = 5;
export const ACTIVE_COSTING_PRICE_DAYS = 60;

export interface ResultActivationCheck {
  /** Racha mas larga de dias seguidos con ventas dentro del periodo. */
  salesDays: number;
  revenue: number;
  expenses: number;
  /** Gastos / ventas del periodo; 0 si no hay ventas. */
  expenseRatio: number;
  met: boolean;
}

const nextDay = (date: string): string => {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
};

/**
 * Hito 1, condiciones 1 y 2, sobre el periodo que el usuario esta mirando.
 * Solo cuentan facturas: un albaran nunca es gasto contable (ver loadFinance).
 */
export function checkResultActivation(data: {
  revenues: DailyRevenue[]; expenses: ExpenseEntry[]; period: { start: string; end: string };
}): ResultActivationCheck {
  const inPeriod = (date: string) => date >= data.period.start && date <= data.period.end;
  const byDay = new Map<string, number>();
  for (const r of data.revenues) {
    if (!inPeriod(r.date)) continue;
    byDay.set(r.date, (byDay.get(r.date) ?? 0) + revenueOf(r));
  }
  const days = [...byDay].filter(([, amount]) => amount > 0).map(([date]) => date).sort();
  let salesDays = 0;
  let run = 0;
  for (let i = 0; i < days.length; i++) {
    run = i > 0 && nextDay(days[i - 1]) === days[i] ? run + 1 : 1;
    salesDays = Math.max(salesDays, run);
  }
  const revenue = [...byDay.values()].filter(v => v > 0).reduce((a, b) => a + b, 0);
  const expenses = data.expenses
    .filter(e => (e.documentType ?? 'factura') === 'factura' && e.netAmount > 0 && inPeriod(e.date))
    .reduce((a, e) => a + e.netAmount, 0);
  const expenseRatio = revenue > 0 ? expenses / revenue : 0;
  return {
    salesDays, revenue, expenses, expenseRatio,
    met: salesDays >= ACTIVATION_SALES_DAYS && revenue > 0 && expenseRatio >= ACTIVATION_EXPENSE_RATIO,
  };
}

export interface CostingStatus {
  active: boolean;
  /** Platos con costeo completo del mejor local. */
  dishes: number;
  locations: { locationId: string; dishes: number; active: boolean }[];
}

/** Hito 2: costeo activo, por local. */
export function activeCostingStatus(data: {
  ingredients: Ingredient[]; recipes: Recipe[]; menuItems: MenuItem[]; now?: Date;
}): CostingStatus {
  const now = data.now ?? new Date();
  const limit = new Date(now.getTime() - ACTIVE_COSTING_PRICE_DAYS * 86400000).toISOString();
  // Fuera del contexto todo ingrediente sin precio real o con precio viejo:
  // una receta que use alguno queda «partial» y el plato no cuenta.
  const fresh = data.ingredients.filter(i => !i.pricePending && !i.priceEstimated
    && (i.netPrice ?? i.prices?.[0] ?? 0) > 0 && (i.updatedAt ?? '') >= limit);
  const ctx = buildContext(fresh, data.recipes);
  const recipesById = new Map(data.recipes.map(r => [r.recipeId, r]));
  const fullyCosted = new Map<string, boolean>();
  const costed = (recipeId: string): boolean => {
    if (!fullyCosted.has(recipeId)) {
      let ok = false;
      try {
        const cost = computeRecipeCost(recipeId, ctx);
        ok = cost.status === 'complete' && !cost.costEstimated && cost.baseCost > 0;
      } catch { /* receta incompleta: no cuenta */ }
      fullyCosted.set(recipeId, ok);
    }
    return fullyCosted.get(recipeId)!;
  };
  const locationIds = new Set<string>([DEFAULT_LOCATION_ID]);
  for (const item of data.menuItems) for (const id of Object.keys(item.unitsSoldByLocation ?? {})) locationIds.add(id);
  const locations = [...locationIds].map(locationId => {
    const dishes = data.menuItems.filter(item => {
      const recipe = recipesById.get(item.recipeId);
      return item.active && recipe?.active && recipe.components.length > 0 && item.sellingPriceGross > 0
        && unitsSoldFor(item, locationId) > 0 && costed(item.recipeId);
    }).length;
    return { locationId, dishes, active: dishes >= ACTIVE_COSTING_DISHES };
  });
  const dishes = Math.max(0, ...locations.map(l => l.dishes));
  return { active: locations.some(l => l.active), dishes, locations };
}

/**
 * Progreso del onboarding (pasos del panel y correos de bienvenida). Ya NO es
 * la definicion de activado: eso es `checkResultActivation` (hito 1).
 */
export function activationPath(data: {
  ingredients: Ingredient[]; recipes: Recipe[]; menuItems: MenuItem[];
  revenues: DailyRevenue[]; expenses: ExpenseEntry[]; settings: TenantSettings;
}): OnboardingGoal | undefined {
  const months = new Set(data.revenues.filter(r => revenueOf(r) > 0).map(r => r.date.slice(0, 7)));
  if (data.expenses.some(e => (e.documentType ?? 'factura') === 'factura' && e.netAmount > 0 && months.has(e.date.slice(0, 7)))) return 'numbers';
  const ctx = buildContext(data.ingredients.filter(i => !i.pricePending && !i.priceEstimated), data.recipes);
  const recipesById = new Map(data.recipes.map(r => [r.recipeId, r]));
  for (const item of data.menuItems) {
    const recipe = recipesById.get(item.recipeId);
    if (!item.active || !recipe?.active || !recipe.components.length || item.sellingPriceGross <= 0) continue;
    try {
      const cost = computeRecipeCost(item.recipeId, ctx);
      if (cost.status === 'partial' || cost.costEstimated || cost.baseCost <= 0) continue;
      if (Number.isFinite(analyzePricing(item, cost, data.settings).grossMargin)) return 'costs';
    } catch { /* An incomplete recipe is not an activation milestone. */ }
  }
}

type GrowthRow = Pick<AdminTenantRow, 'tenantId' | 'createdAt' | 'internal' | 'founderPending' | 'commercialEvents' | 'lastCommercialEvent' | 'lastCommercialEventAt' | 'acquisition' | 'activation' | 'signupFlow' | 'firstPaidAt' | 'canceledAt' | 'subscriptionStatus'>;
export function firstPaidAt(row: GrowthRow): string | undefined {
  return row.firstPaidAt ?? row.commercialEvents?.filter(e => e.type === 'alta').map(e => e.at).sort()[0]
    ?? (row.lastCommercialEvent === 'alta' ? row.lastCommercialEventAt : undefined);
}
export function growthMetrics(rows: GrowthRow[], now = new Date()) {
  const real = rows.filter(r => !r.internal);
  const day = (s: string) => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Madrid' }).format(new Date(s));
  // El objetivo de activacion es mensual (Pol, 23 sep 2026): la serie diaria
  // de 30 dias se quito porque con las cohortes por mes ya sobraba.
  const mesActual = day(now.toISOString()).slice(0, 7);
  // Una activacion "historica" no tiene fecha... salvo que la cuenta se haya
  // creado este mismo mes: entonces activarse este mes era lo unico posible,
  // y contarla no es inventar nada. Sin esto, el objetivo mensual ignoraba a
  // casi toda la cohorte del mes (23 sep 2026: decia 1 cuando eran 17).
  const activadosEsteMes = real.filter(r => r.activation && (
    r.activation.activatedAt ? day(r.activation.activatedAt).startsWith(mesActual) : day(r.createdAt).startsWith(mesActual)
  )).length;
  const cohorts = [...new Set(real.map(r => r.createdAt.slice(0, 7)))].sort().reverse().map(month => {
    const cohort = real.filter(r => r.createdAt.startsWith(month));
    const activated = cohort.filter(r => r.activation);
    const days = activated.filter(r => r.activation?.activatedAt).map(r => Math.max(0, (Date.parse(r.activation!.activatedAt!) - Date.parse(r.createdAt)) / 86400000));
    return { month, total: cohort.length, activated: activated.length, pct: activated.length / cohort.length * 100,
      averageDays: days.length ? days.reduce((a, b) => a + b, 0) / days.length : null, dated: days.length };
  });
  const sources = [...new Set(real.map(r => r.acquisition?.source ?? 'Sin atribución'))].sort().map(source => {
    const accounts = real.filter(r => (r.acquisition?.source ?? 'Sin atribución') === source);
    const paid = accounts.filter(r => firstPaidAt(r)).length;
    return { source, total: accounts.length, paid, pct: paid / accounts.length * 100 };
  });
  return { activadosEsteMes, cohorts, sources, historical: real.filter(r => r.activation?.historical).length };
}

/** Suma k meses a un 'YYYY-MM'. */
const sumaMeses = (month: string, k: number): string => {
  const [y, m] = month.split('-').map(Number);
  const total = y * 12 + (m - 1) + k;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
};

export interface RetentionCohort {
  /** Mes del primer pago de la cohorte (YYYY-MM). */
  month: string;
  total: number;
  /** Una celda por mes natural desde el del primer pago hasta el actual. */
  cells: { month: string; retained: number; pct: number }[];
}

/**
 * Cohortes de retencion de pago: cuentas agrupadas por el mes de su PRIMER
 * pago y cuantas seguian pagando al acabar cada mes posterior. El historial
 * comercial (altas y bajas por evento) existe desde sep 2026; de antes solo
 * consta la ULTIMA baja (`canceledAt`), asi que los meses previos son la
 * mejor aproximacion posible, no un extracto. Las fotos PCOHORT# mensuales
 * del scheduler daran la version exacta hacia delante.
 */
export function retentionCohorts(rows: GrowthRow[], now = new Date()): RetentionCohort[] {
  const mesActual = now.toISOString().slice(0, 7);
  const real = rows.filter(r => !r.internal);
  const seguiaPagando = (r: GrowthRow, month: string): boolean => {
    // La ultima alta o baja registrada hasta ese mes decide; `canceledAt` se
    // trata como una baja mas por si es anterior al historial de eventos.
    const timeline = [
      ...(r.commercialEvents ?? []).filter(e => e.type === 'alta' || e.type === 'baja'),
      ...(r.canceledAt ? [{ type: 'baja' as const, at: r.canceledAt }] : []),
    ].filter(e => e.at.slice(0, 7) <= month).sort((a, b) => a.at.localeCompare(b.at));
    const ultimo = timeline[timeline.length - 1];
    if (ultimo) return ultimo.type === 'alta';
    // Sin nada registrado hasta ese mes: en el mes actual manda el estado
    // vivo de la suscripcion; hacia atras, pagar sin baja conocida es pagar.
    if (month === mesActual) return r.subscriptionStatus === 'active' || r.subscriptionStatus === 'past_due';
    return true;
  };
  const primerPago = new Map(real.map(r => [r, firstPaidAt(r)?.slice(0, 7)] as const));
  const meses = [...new Set([...primerPago.values()].filter((m): m is string => Boolean(m) && m! <= mesActual))].sort();
  return meses.map(month => {
    const cohort = real.filter(r => primerPago.get(r) === month);
    const cells: RetentionCohort['cells'] = [];
    for (let m = month; m <= mesActual; m = sumaMeses(m, 1)) {
      const retained = cohort.filter(r => seguiaPagando(r, m)).length;
      cells.push({ month: m, retained, pct: retained / cohort.length * 100 });
    }
    return { month, total: cohort.length, cells };
  });
}
